import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React from 'react';

import { DataAPIContext } from '../api';
import DeleteIconButton from '../components/DeleteIconButton';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import { GET_LIST, GET_MANY } from '../api/dataProvider';
import { previewAudienceSaved, previewAudienceUnsaved } from './campaignStorage';

/**
 * Audience section: filter builder on top, live audience table below.
 *
 * The filter rows are a simple JSON list `[ { column, operator, value } ]`
 * that the backend validates against the whitelist in
 * ``apiv2/lib/campaign_filters.py``. Rather than trying to be clever about
 * labels/types, we render each operator with a value editor driven by the
 * operator's declared ``value_type`` returned by the ``/filter_options/``
 * endpoint.
 *
 * Audience preview is debounced to avoid hammering the API while the user
 * is still tweaking filters.
 */

const DEBOUNCE_MS = 450;
const PAGE_SIZE = 10;

/**
 * True when a filter row is missing the value its operator needs (CP-8076).
 *
 * A row without a value silently matches nothing/everything (the backend's
 * ``op.resolve`` returns None and skips it), so an operator who added a
 * "Travel date after ..." row but never picked the date would send to a much
 * wider audience than they built. The wizard blocks leaving the Audience
 * step while any row is incomplete; the Send step re-checks as a backstop.
 *
 * ``columns`` is the same registry list the builder renders from
 * (``/filter_options/``); rows whose column/operator can't be resolved are
 * treated as complete here — the backend rejects unknown keys on save with
 * its own error, which is the more accurate surface for that failure.
 */
export const isFilterRowIncomplete = (row, columns) => {
    if (!row) return false;
    const col = (columns || []).find((c) => c.key === row.column);
    const op = col?.operators?.find((o) => o.key === row.operator) || col?.operators?.[0];
    if (!op) return false;
    const valueType = op.value_type || 'string';
    const value = row.value;
    switch (valueType) {
        case 'none':
            return false;
        case 'string':
            // With a choices dropdown, '' is the explicit "— none —" pick and
            // counts as answered; only a never-touched null is incomplete.
            if (col?.value_choices?.length) return value == null;
            return !(typeof value === 'string' && value.trim());
        case 'date':
            return !(typeof value === 'string' && value.trim());
        case 'date_range': {
            const rv = value && typeof value === 'object' ? value : {};
            return !(rv.from || '').trim() && !(rv.to || '').trim();
        }
        case 'date_relative': {
            // The editor displays a "3 days" default but stores null until
            // the operator touches it — require an explicit choice.
            const rv = value && typeof value === 'object' ? value : null;
            return !rv || !Number.isFinite(Number(rv.amount)) || Number(rv.amount) <= 0;
        }
        case 'int':
            return value == null || !Number.isFinite(Number(value));
        case 'multi_id':
        case 'multi_ota':
        case 'multi_tour':
        case 'choice_multi':
        case 'tag_multi':
        case 'multi_string':
        case 'multi_value':
            return !Array.isArray(value) || value.length === 0;
        default:
            return value == null;
    }
};

const useDebounced = (value, ms) => {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const h = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(h);
    }, [value, ms]);
    return debounced;
};

// Free-text "comma separated" editor for list value_types (multi_id, multi_string).
//
// We keep the raw text the user is typing in local state and render *that* — we
// do NOT re-derive the input from the parsed array on every keystroke. A fully
// controlled input over the parsed array strips any in-progress separator (a
// trailing comma has no number/token after it, so it gets filtered out and the
// re-joined value drops it), which makes pressing "," appear to do nothing until
// a second value is typed (CP-8029). ``parseItem`` maps one trimmed token to its
// stored form, returning null to drop invalid tokens.
const CommaListField = ({ value, onChange, label, disabled, parseItem, fullWidth, error }) => {
    const parse = React.useCallback(
        (raw) =>
            raw
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map(parseItem)
                .filter((v) => v !== null && v !== undefined),
        [parseItem],
    );
    const [text, setText] = React.useState(() => (Array.isArray(value) ? value.join(', ') : ''));

    // Re-sync the visible text only when the parent value no longer matches what
    // the current text parses to (row reset, operator switch, external edit) —
    // never on the round-trip from our own onChange, so trailing commas survive.
    React.useEffect(() => {
        const incoming = Array.isArray(value) ? value : [];
        const parsed = parse(text);
        const same =
            parsed.length === incoming.length && parsed.every((v, i) => v === incoming[i]);
        if (!same) {
            setText(incoming.join(', '));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <TextField
            size="small"
            variant="filled"
            fullWidth={fullWidth}
            label={label}
            value={text}
            onChange={(e) => {
                setText(e.target.value);
                onChange(parse(e.target.value));
            }}
            disabled={disabled}
            error={error}
        />
    );
};

// Server-searched multi-select for the OTA column (value_type ``multi_ota``).
// The Online_Travel_Agency table is global and ~10K rows, so the registry
// can't ship static choices and a raw ID editor is unusable (OTA ids aren't
// discoverable anywhere in the dashboard — a tag URL's trailing id is a tag
// pk, not an OTA id). Options come from /edit/ota/ name search; names for
// already-saved ids are resolved once via GET_MANY so chips never show bare
// numbers when reopening a campaign.
const OtaMultiPicker = ({ value, onChange, disabled, error }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [input, setInput] = React.useState('');
    const [options, setOptions] = React.useState([]);
    // id -> name cache in a ref so the resolve effect doesn't depend on its
    // own output (each write also bumps a counter to re-render the chips).
    const labelsRef = React.useRef({});
    const [, bumpLabels] = React.useReducer((x) => x + 1, 0);
    const debouncedInput = useDebounced(input, 300);
    const ids = Array.isArray(value)
        ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n))
        : [];
    const idsKey = ids.join(',');

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await dataAPI(GET_LIST, 'ota', {
                    filter: { query: debouncedInput || '' },
                    pagination: { page: 1, perPage: 20 },
                    sort: { field: 'name', order: 'ASC' },
                });
                if (cancelled) return;
                const opts = (res?.data || []).map((o) => ({ value: o.id, label: o.name }));
                opts.forEach((o) => { labelsRef.current[o.value] = o.label; });
                setOptions(opts);
                bumpLabels();
            } catch (e) {
                // Keep the previous options; the picker stays usable offline-ish.
            }
        })();
        return () => { cancelled = true; };
    }, [dataAPI, debouncedInput]);

    React.useEffect(() => {
        const missing = idsKey
            .split(',')
            .filter(Boolean)
            .map(Number)
            .filter((id) => labelsRef.current[id] === undefined);
        if (!missing.length) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await dataAPI(GET_MANY, 'ota', { ids: missing });
                if (cancelled) return;
                (res?.data || []).forEach((o) => { labelsRef.current[o.id] = o.name; });
            } catch (e) {
                if (cancelled) return;
                // Unresolvable (deleted id, transient error): pin a fallback so
                // we don't refetch on every render.
                missing.forEach((id) => { labelsRef.current[id] = `OTA #${id}`; });
            }
            bumpLabels();
        })();
        return () => { cancelled = true; };
    }, [dataAPI, idsKey]);

    const selected = ids.map((id) => ({ value: id, label: labelsRef.current[id] ?? `OTA #${id}` }));
    return (
        <Autocomplete
            multiple
            size="small"
            options={options}
            // Options are already server-filtered; client-side label matching
            // would hide results whose names don't contain the raw input.
            filterOptions={(o) => o}
            disabled={disabled}
            value={selected}
            getOptionLabel={(o) => o.label || String(o.value)}
            isOptionEqualToValue={(a, b) => Number(a.value) === Number(b.value)}
            onChange={(_, picked) =>
                onChange(
                    picked
                        .map((p) => Number(p.value))
                        .filter((n) => Number.isFinite(n)),
                )
            }
            inputValue={input}
            onInputChange={(_, v, reason) => {
                if (reason !== 'reset') setInput(v);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="filled"
                    label="OTAs"
                    placeholder="Search OTAs"
                    error={error}
                />
            )}
            sx={{ minWidth: 280 }}
        />
    );
};

// Server-searched multi-select for the Tour column (value_type ``multi_tour``).
// Tours are operator-scoped, but an operator can have thousands of them, so the
// registry can't ship static choices — and the raw-id editor it replaces asked
// for Group_Tour ids that are only discoverable by reading them off a trips-grid
// URL, with no confirmation of what was picked until the audience preview came
// back wrong.
//
// Options come from /edit/tour/autocomplete/ (already tour-operator scoped, and
// franchise-scoped by the licensee injection in api/index.js). Names for
// already-saved ids are resolved in one extra call using that endpoint's ``id:``
// text-query prefix — GET_MANY would be one request per id against the full
// tour serializer — so chips never show bare numbers when reopening a campaign.
const TOUR_OPTION_LIMIT = 50;

// Bespoke tours are one Group_Tour per booking, so an operator can have dozens
// that share a name — the dates are what tell them apart (same convention as
// the Trip Items "Select Trip" picker). Recurring tours are unique by name.
// CP-6115: same "[id:1234] Name (...)" label as every other tour picker.
const tourLabel = (t) => formatTourOptionLabel(t);

const TourMultiPicker = ({ value, onChange, disabled, error }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [input, setInput] = React.useState('');
    const [options, setOptions] = React.useState([]);
    // id -> name cache in a ref so the resolve effect doesn't depend on its
    // own output (each write also bumps a counter to re-render the chips).
    const labelsRef = React.useRef({});
    const [, bumpLabels] = React.useReducer((x) => x + 1, 0);
    const debouncedInput = useDebounced(input, 300);
    const ids = Array.isArray(value)
        ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n))
        : [];
    const idsKey = ids.join(',');

    const fetchTours = React.useCallback(
        (query) => dataAPI(GET_LIST, 'tour', {
            urlSuffix: 'autocomplete/',
            // ``fields`` keeps the autocomplete serializer off tag categories and
            // departure times — the picker only needs what ``tourLabel`` reads.
            filter: { query, fields: 'id,name,is_bespoke,start_date,end_date' },
            pagination: { page: 1, perPage: TOUR_OPTION_LIMIT },
            sort: { field: 'name', order: 'ASC' },
        }),
        [dataAPI],
    );

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchTours(debouncedInput || '');
                if (cancelled) return;
                const opts = (res?.data || []).map((t) => ({ value: t.id, label: tourLabel(t) }));
                opts.forEach((o) => { labelsRef.current[o.value] = o.label; });
                // The autocomplete action serializes the whole matching queryset
                // rather than a page, so cap what we hand MUI — an unvirtualized
                // list of every tour an operator has ever run janks the dropdown.
                setOptions(opts.slice(0, TOUR_OPTION_LIMIT));
                bumpLabels();
            } catch (e) {
                // Keep the previous options; the picker stays usable.
            }
        })();
        return () => { cancelled = true; };
    }, [fetchTours, debouncedInput]);

    React.useEffect(() => {
        const missing = idsKey
            .split(',')
            .filter(Boolean)
            .map(Number)
            .filter((id) => labelsRef.current[id] === undefined);
        if (!missing.length) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchTours(missing.map((id) => `id:${id}`).join(' '));
                if (cancelled) return;
                (res?.data || []).forEach((t) => { labelsRef.current[t.id] = tourLabel(t); });
            } catch (e) {
                if (cancelled) return;
            }
            if (cancelled) return;
            // Whatever didn't come back (deleted tour, another operator's id, a
            // transient error): pin a fallback so we don't refetch every render.
            missing.forEach((id) => {
                if (labelsRef.current[id] === undefined) {
                    labelsRef.current[id] = tourLabel(id);
                }
            });
            bumpLabels();
        })();
        return () => { cancelled = true; };
    }, [fetchTours, idsKey]);

    const selected = ids.map((id) => ({ value: id, label: labelsRef.current[id] ?? tourLabel(id) }));
    return (
        <Autocomplete
            multiple
            size="small"
            options={options}
            // Options are already server-filtered; client-side label matching
            // would hide results whose names don't contain the raw input.
            filterOptions={(o) => o}
            disabled={disabled}
            value={selected}
            getOptionLabel={(o) => o.label || String(o.value)}
            isOptionEqualToValue={(a, b) => Number(a.value) === Number(b.value)}
            onChange={(_, picked) =>
                onChange(
                    picked
                        .map((p) => Number(p.value))
                        .filter((n) => Number.isFinite(n)),
                )
            }
            inputValue={input}
            onInputChange={(_, v, reason) => {
                if (reason !== 'reset') setInput(v);
            }}
            // The label carries the id, so a pick can be confirmed against the
            // old "IDs" box — and typing ``id:2829`` in the search still finds it.
            renderOption={(props, option) => (
                <li {...props} key={option.value}>
                    {option.label}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="filled"
                    label="Tours"
                    placeholder="Search tours (name or id:1234)"
                    error={error}
                />
            )}
            sx={{ minWidth: 320 }}
        />
    );
};

const ValueEditor = ({ valueType, value, onChange, valueChoices, disabled, error }) => {
    if (valueType === 'none') {
        return null;
    }
    if (valueType === 'string' && valueChoices && valueChoices.length) {
        return (
            <FormControl variant="filled" size="small" sx={{ minWidth: 160 }} disabled={disabled} error={error}>
                <InputLabel>Value</InputLabel>
                <Select value={value ?? ''} label="Value" onChange={(e) => onChange(e.target.value)}>
                    <MenuItem value="">
                        <em>— none —</em>
                    </MenuItem>
                    {valueChoices.map((c) => (
                        <MenuItem key={c.value} value={c.value}>
                            {c.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }
    if (valueType === 'string') {
        return (
            <TextField
                size="small"
                variant="filled"
                label="Value"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                error={error}
            />
        );
    }
    if (valueType === 'date') {
        return (
            <TextField
                size="small"
                variant="filled"
                label="Date"
                type="date"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                error={error}
            />
        );
    }
    if (valueType === 'date_range') {
        const rv = value && typeof value === 'object' ? value : {};
        return (
            <Stack direction="row" spacing={1}>
                <TextField
                    size="small"
                    variant="filled"
                    label="From"
                    type="date"
                    value={rv.from ?? ''}
                    onChange={(e) => onChange({ ...rv, from: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    error={error}
                />
                <TextField
                    size="small"
                    variant="filled"
                    label="To"
                    type="date"
                    value={rv.to ?? ''}
                    onChange={(e) => onChange({ ...rv, to: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    error={error}
                />
            </Stack>
        );
    }
    if (valueType === 'date_relative') {
        const rv = value && typeof value === 'object' ? value : { amount: 3, unit: 'days' };
        return (
            <Stack direction="row" spacing={1}>
                <TextField
                    size="small"
                    variant="filled"
                    label="Amount"
                    type="number"
                    value={rv.amount ?? ''}
                    onChange={(e) => onChange({ ...rv, amount: Number(e.target.value) })}
                    sx={{ width: 100 }}
                    disabled={disabled}
                    error={error}
                />
                <FormControl variant="filled" size="small" sx={{ minWidth: 120 }} disabled={disabled}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                        value={rv.unit || 'days'}
                        label="Unit"
                        onChange={(e) => onChange({ ...rv, unit: e.target.value })}
                    >
                        <MenuItem value="days">Days</MenuItem>
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="months">Months</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
        );
    }
    if (valueType === 'multi_id') {
        if (valueChoices && valueChoices.length) {
            const arr = Array.isArray(value) ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n)) : [];
            return (
                <FormControl variant="filled" size="small" sx={{ minWidth: 200 }} disabled={disabled} error={error}>
                    <InputLabel>Value</InputLabel>
                    <Select
                        multiple
                        value={arr}
                        label="Value"
                        onChange={(e) => {
                            const next = e.target.value;
                            onChange(
                                (Array.isArray(next) ? next : [next])
                                    .map((v) => Number(v))
                                    .filter((n) => Number.isFinite(n)),
                            );
                        }}
                        renderValue={(selected) =>
                            (selected || [])
                                .map((v) => valueChoices.find((c) => Number(c.value) === Number(v))?.label || v)
                                .join(', ')
                        }
                    >
                        {valueChoices.map((c) => (
                            <MenuItem key={c.value} value={Number(c.value)}>
                                <Checkbox checked={arr.indexOf(Number(c.value)) > -1} />
                                {c.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
        return (
            <CommaListField
                label="IDs (comma separated)"
                value={value}
                onChange={onChange}
                disabled={disabled}
                error={error}
                parseItem={(s) => {
                    const n = Number(s);
                    return Number.isFinite(n) ? n : null;
                }}
            />
        );
    }
    if (valueType === 'multi_ota') {
        return (
            <OtaMultiPicker value={value} onChange={onChange} disabled={disabled} error={error} />
        );
    }
    if (valueType === 'multi_tour') {
        return (
            <TourMultiPicker value={value} onChange={onChange} disabled={disabled} error={error} />
        );
    }
    if (valueType === 'choice_multi') {
        // Searchable chip picker over choices the payload already carries — for
        // columns whose whole option set is small enough to ship inline (access
        // tags: 25 at the largest operator). Same look as the tag and tour
        // pickers, but no fetching: labels for saved ids resolve straight out
        // of ``valueChoices``.
        const choices = Array.isArray(valueChoices) ? valueChoices : [];
        if (!choices.length) {
            // A payload with no choices (no operator scope resolved) would
            // otherwise leave an empty picker with no way to enter anything.
            return (
                <CommaListField
                    label="IDs (comma separated)"
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    error={error}
                    parseItem={(s) => {
                        const n = Number(s);
                        return Number.isFinite(n) ? n : null;
                    }}
                />
            );
        }
        const selectedIds = Array.isArray(value)
            ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n))
            : [];
        // An id with no matching choice (a tag deleted since the campaign was
        // saved) keeps its own option so it stays visible and removable rather
        // than vanishing from a filter that still applies it.
        const selectedOptions = selectedIds.map(
            (id) => choices.find((c) => Number(c.value) === id) || { value: id, label: `#${id}` },
        );
        return (
            <Autocomplete
                multiple
                size="small"
                options={choices}
                disabled={disabled}
                value={selectedOptions}
                getOptionLabel={(o) => o.label || String(o.value)}
                isOptionEqualToValue={(o, v) => Number(o.value) === Number(v.value)}
                onChange={(_, picked) =>
                    onChange(
                        picked
                            .map((p) => Number(p.value))
                            .filter((n) => Number.isFinite(n)),
                    )
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="filled"
                        label="Value"
                        placeholder="Search"
                        error={error}
                    />
                )}
                sx={{ minWidth: 280 }}
            />
        );
    }
    if (valueType === 'tag_multi') {
        // Category-grouped, searchable multi-select. Options carry
        // {value, label, group}; we store/emit an array of numeric tag ids
        // (same shape the backend's ``_op_multi_in`` expects). Grouping +
        // search keep the picker usable when an operator has hundreds of tags.
        const choices = Array.isArray(valueChoices) ? valueChoices : [];
        const selectedIds = Array.isArray(value)
            ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n))
            : [];
        const selectedOptions = selectedIds
            .map((id) => choices.find((c) => Number(c.value) === id))
            .filter(Boolean);
        return (
            <Autocomplete
                multiple
                size="small"
                options={choices}
                disabled={disabled}
                value={selectedOptions}
                groupBy={(o) => o.group || ''}
                getOptionLabel={(o) => o.label || String(o.value)}
                isOptionEqualToValue={(o, v) => Number(o.value) === Number(v.value)}
                onChange={(_, picked) =>
                    onChange(
                        picked
                            .map((p) => Number(p.value))
                            .filter((n) => Number.isFinite(n)),
                    )
                }
                // Selected chips carry the category prefix — the dropdown is
                // grouped so bare labels are unambiguous there, but a chip for
                // "Viator" alone doesn't say whether it's the OTA or the
                // Reseller tag.
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            size="small"
                            label={
                                option.group
                                    ? `${option.group}: ${option.label}`
                                    : option.label || String(option.value)
                            }
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="filled"
                        label="Tags"
                        placeholder="Search tags"
                        error={error}
                    />
                )}
                sx={{ minWidth: 280 }}
            />
        );
    }
    if (valueType === 'multi_string') {
        // Comma-separated list of free-text values (e.g. a batch of emails to
        // exclude). Same editor as multi_id but tokens stay strings — no
        // Number() coercion. Backend matches case-insensitively.
        return (
            <CommaListField
                label="Emails (comma separated)"
                value={value}
                onChange={onChange}
                disabled={disabled}
                fullWidth
                error={error}
                parseItem={(s) => s}
            />
        );
    }
    if (valueType === 'int') {
        return (
            <TextField
                size="small"
                variant="filled"
                label="Value"
                type="number"
                value={value ?? ''}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return onChange(null);
                    const n = Number(raw);
                    onChange(Number.isFinite(n) ? n : null);
                }}
                disabled={disabled}
                error={error}
            />
        );
    }
    if (valueType === 'multi_value') {
        // Same UX as multi_id but values are passed through as-is (string),
        // used for enum-style choice fields like channel-mode "Tour status"
        // where the backend expects {"active","upcoming","past"} strings.
        const choices = Array.isArray(valueChoices) ? valueChoices : [];
        const arr = Array.isArray(value) ? value.map((v) => String(v)) : [];
        return (
            <FormControl variant="filled" size="small" sx={{ minWidth: 200 }} disabled={disabled} error={error}>
                <InputLabel>Value</InputLabel>
                <Select
                    multiple
                    value={arr}
                    label="Value"
                    onChange={(e) => {
                        const next = e.target.value;
                        onChange(
                            (Array.isArray(next) ? next : [next])
                                .map((v) => String(v))
                                .filter((v) => v !== ''),
                        );
                    }}
                    renderValue={(selected) =>
                        (selected || [])
                            .map((v) => choices.find((c) => String(c.value) === String(v))?.label || v)
                            .join(', ')
                    }
                >
                    {choices.map((c) => (
                        <MenuItem key={String(c.value)} value={String(c.value)}>
                            <Checkbox checked={arr.indexOf(String(c.value)) > -1} />
                            {c.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }
    return null;
};

const FilterRow = ({ columns, row, onChange, onRemove, disabled, invalid }) => {
    const col = columns.find((c) => c.key === row.column) || null;
    const op = col?.operators?.find((o) => o.key === row.operator) || col?.operators?.[0] || null;

    const handleColumnChange = (newKey) => {
        const newCol = columns.find((c) => c.key === newKey) || null;
        const newOp = newCol?.operators?.[0] || null;
        onChange({
            column: newKey,
            operator: newOp ? newOp.key : '',
            value: null,
        });
    };

    const handleOperatorChange = (newKey) => {
        onChange({ ...row, operator: newKey, value: null });
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <FormControl variant="filled" size="small" sx={{ minWidth: 180 }} disabled={disabled}>
                    <InputLabel>Column</InputLabel>
                    <Select value={row.column} label="Column" onChange={(e) => handleColumnChange(e.target.value)}>
                        {columns.map((c) => (
                            <MenuItem key={c.key} value={c.key}>
                                {c.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="filled" size="small" sx={{ minWidth: 160 }} disabled={disabled || !col}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                        value={row.operator}
                        label="Operator"
                        onChange={(e) => handleOperatorChange(e.target.value)}
                    >
                        {(col?.operators || []).map((o) => (
                            <MenuItem key={o.key} value={o.key}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <ValueEditor
                    valueType={op?.value_type || 'string'}
                    value={row.value}
                    onChange={(v) => onChange({ ...row, value: v })}
                    valueChoices={col?.value_choices || null}
                    disabled={disabled}
                    error={invalid}
                />
                <DeleteIconButton title="Remove filter" size="medium" fontSize="medium" onClick={onRemove} disabled={disabled} />
            </Stack>
            {invalid && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    This filter needs a value — fill it in or remove the row.
                </Typography>
            )}
        </Box>
    );
};

const CampaignAudienceBuilder = ({
    campaignId,
    filters,
    onFiltersChange,
    filterRegistry,
    disabled,
    onAudienceCount,
    mode = 'user',
    requireConversation = false,
    purpose = null,
    includeCancelledBookings = null,
    // When true (the wizard blocked navigation on this step), incomplete
    // rows render their value editors in the error state with a hint.
    showValidation = false,
}) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [preview, setPreview] = React.useState({ count: 0, results: [] });
    const [page, setPage] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState(null);

    const columns = filterRegistry?.columns || [];
    const debouncedFilters = useDebounced(filters, DEBOUNCE_MS);
    const isChannelMode = mode === 'channel';
    const isMainOfficeMode = mode === 'main_office';

    const addRow = () => {
        const firstCol = columns[0];
        if (!firstCol) return;
        const firstOp = firstCol.operators?.[0];
        const next = [
            ...filters,
            { column: firstCol.key, operator: firstOp ? firstOp.key : '', value: null },
        ];
        onFiltersChange(next);
    };

    const updateRow = (idx, newRow) => {
        const next = filters.slice();
        next[idx] = newRow;
        onFiltersChange(next);
    };

    const removeRow = (idx) => {
        const next = filters.slice();
        next.splice(idx, 1);
        onFiltersChange(next);
    };

    // Debounced preview fetch. We intentionally use the unsaved preview
    // endpoint so "what-if" changes appear live without persisting filters.
    React.useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!columns.length) return;
            setLoading(true);
            setErr(null);
            try {
                const pageForQuery = Math.max(1, page);
                const data = campaignId
                    ? await previewAudienceSaved(dataAPI, campaignId, {
                        filters: debouncedFilters,
                        page: pageForQuery,
                        per_page: PAGE_SIZE,
                        mode,
                        require_conversation: requireConversation,
                        purpose,
                        include_cancelled_bookings: includeCancelledBookings,
                    })
                    : await previewAudienceUnsaved(dataAPI, {
                        filters: debouncedFilters,
                        page: pageForQuery,
                        per_page: PAGE_SIZE,
                        mode,
                        require_conversation: requireConversation,
                        purpose,
                        include_cancelled_bookings: includeCancelledBookings,
                    });
                if (cancelled) return;
                setPreview(data);
                if (typeof onAudienceCount === 'function') onAudienceCount(data.count ?? 0);
            } catch (e) {
                if (!cancelled) {
                    setErr(e?.message || 'Could not load audience preview');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [campaignId, debouncedFilters, page, dataAPI, columns.length, onAudienceCount, mode, requireConversation, purpose, includeCancelledBookings]);

    // Reset to the first page whenever the mode swaps so the operator
    // doesn't end up paged past the (likely much smaller) channel result
    // set when flipping between user mode and announcement mode.
    React.useEffect(() => {
        setPage(1);
    }, [mode]);

    const totalPages = preview?.count ? Math.max(1, Math.ceil(preview.count / PAGE_SIZE)) : 1;

    const helperText = isChannelMode
        ? 'Announcement mode targets group-tour announcements channels. Each matching channel below receives one post — every participant sees the message regardless of per-user filters.'
        : isMainOfficeMode
            ? 'Main Office mode targets users who already have a live Main Office thread with this tour operator. The campaign posts once per recipient in their MO thread. Defaults to guests — add an "Audience type is guides" row to target guides instead.'
            : 'The campaign audience is every guest on a tour with this tour operator that matches every filter below. Leave the list empty to target all guests.';

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                {helperText}
            </Typography>

            <Stack spacing={1}>
                {filters.map((row, idx) => (
                    <FilterRow
                        key={idx}
                        columns={columns}
                        row={row}
                        onChange={(r) => updateRow(idx, r)}
                        onRemove={() => removeRow(idx)}
                        disabled={disabled}
                        invalid={showValidation && isFilterRowIncomplete(row, columns)}
                    />
                ))}
                <Box>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                        disabled={disabled || !columns.length}
                    >
                        Add filter
                    </Button>
                </Box>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography fontWeight={600}>
                        {isChannelMode ? 'Channels preview' : 'Audience preview'}
                        {' '}
                        <Typography component="span" color="text.secondary" fontWeight={400}>
                            ({loading
                                ? 'loading…'
                                : `${(preview.count || 0).toLocaleString()}${preview.count_capped ? '+' : ''} ${
                                    isChannelMode
                                        ? `channel${preview.count === 1 ? '' : 's'}`
                                        : isMainOfficeMode
                                            ? `recipient${preview.count === 1 ? '' : 's'}`
                                            : `match${preview.count === 1 ? '' : 'es'}`
                                }`})
                        </Typography>
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                            Prev
                        </Button>
                        <Typography variant="caption">
                            Page {page} / {totalPages}
                        </Typography>
                        <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                            Next
                        </Button>
                    </Stack>
                </Box>
                {err && (
                    <Box sx={{ p: 2 }}>
                        <Typography color="error" variant="body2">
                            {String(err)}
                        </Typography>
                    </Box>
                )}
                {requireConversation && (preview?.excluded_no_conversation || 0) > 0 && (
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Alert severity="info" sx={{ py: 0.5 }}>
                            Excluding {Number(preview.excluded_no_conversation).toLocaleString()}{' '}
                            {preview.excluded_no_conversation === 1 ? 'recipient' : 'recipients'}{' '}
                            with no in-app chat conversation. Enable Email to reach them.
                        </Alert>
                    </Box>
                )}
                <TableContainer sx={{ maxWidth: '100%' }}>
                    {isChannelMode ? (
                        <ChannelPreviewTable rows={preview.results || []} loading={loading} />
                    ) : isMainOfficeMode ? (
                        <MainOfficePreviewTable rows={preview.results || []} loading={loading} />
                    ) : (
                        <UserPreviewTable rows={preview.results || []} loading={loading} />
                    )}
                </TableContainer>
            </Paper>
        </Stack>
    );
};

const buildConversationLink = (row) => {
    const gtcc = row?.conversation_group_tour_chat_channel_id;
    const uniqueName = row?.conversation_unique_name;
    if (!gtcc || !uniqueName) return null;
    return `/chat/${gtcc}/conversations/${uniqueName}/interactivechat`;
};

const ConversationLinkCell = ({ row }) => {
    const href = buildConversationLink(row);
    if (!href) {
        return (
            <Typography component="span" color="text.disabled" variant="body2">
                —
            </Typography>
        );
    }
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 13,
                fontWeight: 500,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            Open
            <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
    );
};

const UserPreviewTable = ({ rows, loading }) => (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
            <TableRow>
                <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell sx={{ width: '18%', whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ width: '20%', whiteSpace: 'nowrap' }}>Tour</TableCell>
                <TableCell sx={{ width: '24%', whiteSpace: 'nowrap' }}>Tags</TableCell>
                <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }}>Travel date</TableCell>
                <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">Conversation</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {rows.map((r) => (
                <TableRow key={r.group_tour_user_id}>
                    <TableCell sx={{ width: '14%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.full_name || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '18%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.email || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '20%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {r.group_tour_name || r.group_tour_id || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '24%', whiteSpace: 'normal' }}>
                        <TagsCell tags={r.tags} />
                    </TableCell>
                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }}>
                        {r.travel_date ? new Date(r.travel_date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">
                        <ConversationLinkCell row={r} />
                    </TableCell>
                </TableRow>
            ))}
            {!loading && rows.length === 0 && (
                <TableRow>
                    <TableCell colSpan={6}>
                        <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>
                            No guests match the current filters.
                        </Typography>
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
);

const ChannelPreviewTable = ({ rows, loading }) => (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
            <TableRow>
                <TableCell sx={{ width: '28%', whiteSpace: 'nowrap' }}>Tour</TableCell>
                <TableCell sx={{ width: '34%', whiteSpace: 'nowrap' }}>Tags</TableCell>
                <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>Travel date</TableCell>
                <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">Recipients</TableCell>
                <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">Conversation</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {rows.map((r) => (
                <TableRow key={r.group_tour_chat_channel_id}>
                    <TableCell sx={{ width: '28%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {r.group_tour_name || r.group_tour_id || '—'}
                        {r.tour_status ? (
                            <Chip
                                size="small"
                                label={r.tour_status}
                                sx={{
                                    ml: 1,
                                    height: 18,
                                    fontSize: 10,
                                    textTransform: 'capitalize',
                                    bgcolor: r.tour_status === 'active'
                                        ? '#D1FADF'
                                        : r.tour_status === 'upcoming'
                                            ? '#E0EAFF'
                                            : '#F2F4F7',
                                    color: r.tour_status === 'active'
                                        ? '#027A48'
                                        : r.tour_status === 'upcoming'
                                            ? '#3538CD'
                                            : '#475467',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        ) : null}
                    </TableCell>
                    <TableCell sx={{ width: '34%', whiteSpace: 'normal' }}>
                        <TagsCell tags={r.tags} />
                    </TableCell>
                    <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>
                        {r.travel_date ? new Date(r.travel_date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">
                        {Number(r.recipient_count || 0).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }} align="right">
                        <ConversationLinkCell row={r} />
                    </TableCell>
                </TableRow>
            ))}
            {!loading && rows.length === 0 && (
                <TableRow>
                    <TableCell colSpan={5}>
                        <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>
                            No channels match the current filters.
                        </Typography>
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
);

const MainOfficePreviewTable = ({ rows, loading }) => (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
            <TableRow>
                <TableCell sx={{ width: '22%', whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell sx={{ width: '20%', whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }} align="right">Trips</TableCell>
                <TableCell sx={{ width: '20%', whiteSpace: 'nowrap' }}>Last activity</TableCell>
                <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>Language</TableCell>
                <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }} align="right">Conversation</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {rows.map((r) => (
                <TableRow key={r.twilio_users_by_conversation_id || r.user_id}>
                    <TableCell sx={{ width: '22%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.full_name || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '20%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.email || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }} align="right">
                        {Number(r.trip_count || 0).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ width: '20%', whiteSpace: 'nowrap' }}>
                        {r.latest_message_ts ? new Date(r.latest_message_ts).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>
                        {r.language || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }} align="right">
                        <ConversationLinkCell row={r} />
                    </TableCell>
                </TableRow>
            ))}
            {!loading && rows.length === 0 && (
                <TableRow>
                    <TableCell colSpan={6}>
                        <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>
                            No Main Office threads match the current filters.
                        </Typography>
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
);

const TagsCell = ({ tags }) => {
    if (!Array.isArray(tags) || tags.length === 0) return '—';
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map((t) => (
                <Chip
                    key={t.id}
                    label={
                        // Same tag name can exist in several categories, so an
                        // unprefixed chip is ambiguous (e.g. Viator exists as
                        // both an OTA and a Reseller tag).
                        t.category ? (
                            <>
                                <Box component="span" sx={{ opacity: 0.65, fontWeight: 400 }}>
                                    {t.category}:{' '}
                                </Box>
                                {t.name || `Tag #${t.id}`}
                            </>
                        ) : (
                            t.name || `Tag #${t.id}`
                        )
                    }
                    size="small"
                    sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 1,
                        bgcolor: '#E4E7EC',
                        color: '#475467',
                        fontWeight: 500,
                        fontSize: 12,
                        border: 'none',
                        '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            display: 'block',
                            px: 1,
                            py: 0.25,
                        },
                    }}
                />
            ))}
        </Box>
    );
};

export default CampaignAudienceBuilder;
